<template>
  <div>
    <ClinicPageHeader
      title="Confirmações"
      subtitle="Consultas aguardando confirmação do paciente"
    >
      <template #actions>
        <button
          v-if="canEdit('confirmacoes')"
          class="btn btn--primary btn--sm"
          @click="remindAll"
        >
          Enviar lembrete a todos
        </button>
      </template>
    </ClinicPageHeader>

    <div class="dbody">
      <!-- Erro -->
      <ClinicStateBox
        v-if="state === 'error'"
        state="error"
        title="Não foi possível carregar as confirmações"
        message="Ocorreu um erro ao buscar a fila de confirmações. Tente novamente."
      >
        <template #actions>
          <button class="btn btn--primary btn--sm" @click="reload">
            Tentar de novo
          </button>
        </template>
      </ClinicStateBox>

      <!-- Loading -->
      <ClinicStateBox
        v-else-if="state === 'loading'"
        state="loading"
        :rows="8"
      />

      <!-- Conteúdo -->
      <template v-else>
        <div
          v-if="queue.length"
          class="card"
          style="display:flex;align-items:center;gap:var(--space-4);margin-bottom:var(--space-5);background:var(--color-warning-light);border-color:var(--color-warning)"
        >
          <div
            class="metric-icon"
            style="background:#fff;color:var(--color-warning)"
            v-html="icons.confirm"
          />
          <div style="flex:1">
            <b
              style="font-size:var(--font-size-sm);color:var(--color-gray-900)"
            >
              {{ queue.length }} consultas aguardando confirmação
            </b>
            <div
              style="font-size:var(--font-size-xs);color:var(--color-gray-600)"
            >
              {{ amanhaCount }} são para amanhã. Confirme com os pacientes para
              reduzir faltas.
            </div>
          </div>
          <button
            v-if="canEdit('confirmacoes')"
            class="btn btn--primary btn--sm"
            @click="remindAll"
          >
            Enviar lembrete a todos
          </button>
        </div>

        <ClinicSeg
          v-if="queue.length"
          v-model="filter"
          :options="filters"
          style="margin-bottom:var(--space-5)"
        />

        <div v-if="filteredQueue.length" class="card card--flush">
          <table class="table">
            <thead>
              <tr>
                <th>Paciente</th>
                <th>Profissional de saúde</th>
                <th>Quando</th>
                <th>Status</th>
                <th class="t-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in filteredQueue" :key="item.id">
                <td>
                  <div class="row" style="gap:var(--space-3)">
                    <div class="avatar avatar--sm" :class="item.avatarClass">
                      {{ item.initials }}
                    </div>
                    <div class="stack" style="gap:0">
                      <span class="cell-strong">{{ item.patientName }}</span>
                      <span
                        style="font-size:var(--font-size-xs);color:var(--color-gray-400)"
                      >
                        {{ item.phone }}
                      </span>
                    </div>
                  </div>
                </td>
                <td>{{ item.doctorName }}</td>
                <td class="num">
                  <strong style="color:var(--color-gray-900)">
                    {{ item.whenDay }}
                  </strong>
                  · {{ item.whenTime }}
                </td>
                <td>
                  <span class="badge badge--warning">Aguardando</span>
                </td>
                <td class="t-right">
                  <div v-if="canEdit('confirmacoes')" class="queue-acts">
                    <button
                      v-if="canEdit('reagendamentos')"
                      class="btn btn--outline btn--sm"
                      @click="ask('reschedule', item)"
                    >
                      Reagendar
                    </button>
                    <button
                      class="btn btn--danger btn--sm"
                      @click="ask('cancel', item)"
                    >
                      Cancelar
                    </button>
                    <button
                      class="btn btn--success btn--sm"
                      @click="ask('confirm', item)"
                    >
                      Confirmar
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <ClinicStateBox
          v-else
          state="empty"
          title="Nenhuma consulta a confirmar"
          message="Todas as consultas desta unidade já foram confirmadas."
        />
      </template>
    </div>

    <!-- Confirmação das ações da fila (evita clique na consulta errada) -->
    <ClinicConfirmDialog
      v-if="pending"
      :title="pendingAction.title"
      :confirm-label="pendingAction.confirmLabel"
      :variant="pendingAction.variant"
      :loading="busy"
      @confirm="runPending"
      @close="pending = null"
    >
      {{ pendingAction.lead }} a consulta de {{ pending.item.patientName }} com
      <strong>{{ pending.item.doctorName }}</strong>
      em
      <strong>{{ pending.item.whenDay }} às {{ pending.item.whenTime }}</strong>
      ?
      <template v-if="pending.action === 'confirm'">
        Confirme apenas se o paciente já garantiu que vai comparecer.
      </template>
    </ClinicConfirmDialog>
  </div>
</template>

<script>
import clinicCan from "@/mixins/clinicCan";
import ClinicPageHeader from "@/components/clinica/ClinicPageHeader.vue";
import ClinicStateBox from "@/components/clinica/ClinicStateBox.vue";
import ClinicSeg from "@/components/clinica/ClinicSeg.vue";
import ClinicConfirmDialog from "@/components/clinica/ClinicConfirmDialog.vue";
import { ClinicConfirmations as ConfirmationsService } from "@/services/clinica";

// Textos do modal de confirmação de cada ação da fila.
const ACTIONS = {
  reschedule: {
    title: "Reagendar consulta",
    lead: "Tem certeza de que deseja reagendar",
    confirmLabel: "Sim, reagendar",
    variant: "primary"
  },
  cancel: {
    title: "Cancelar consulta",
    lead: "Tem certeza de que deseja cancelar",
    confirmLabel: "Sim, cancelar",
    variant: "danger"
  },
  confirm: {
    title: "Confirmar consulta",
    lead: "Deseja confirmar",
    confirmLabel: "Sim, confirmar",
    variant: "success"
  }
};

const ICONS = {
  confirm: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 11l3 3L22 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`
};

export default {
  name: "ClinicConfirmations",
  components: {
    ClinicPageHeader,
    ClinicStateBox,
    ClinicSeg,
    ClinicConfirmDialog
  },
  mixins: [clinicCan],
  data() {
    return {
      state: "loading",
      icons: ICONS,
      filter: "todas",
      queue: [],
      // Ação aguardando o modal de confirmação: { action, item }
      pending: null,
      busy: false
    };
  },
  created() {
    this.fetch();
  },
  methods: {
    async fetch() {
      this.state = "loading";
      try {
        const { data } = await ConfirmationsService.list({
          fail: this.$route.query.state === "error"
        });
        this.queue = data.map(c => {
          const [whenDay, whenTime] = (c.when || "").split(" · ");
          return { ...c, whenDay, whenTime };
        });
        this.state = "ready";
      } catch (e) {
        this.state = "error";
      }
    },
    reload() {
      this.fetch();
    },
    async remindAll() {
      // Dispara lembrete (WhatsApp + SMS via Zenvia) a todos os pacientes da fila.
      try {
        await ConfirmationsService.remindAll();
        this.$toast.success("Lembretes enviados aos pacientes");
      } catch (e) {
        this.$toast.error("Não foi possível enviar os lembretes");
      }
    },
    ask(action, item) {
      this.pending = { action, item };
    },
    // Executa a ação confirmada no modal; fecha só se deu certo.
    async runPending() {
      const { action, item } = this.pending;
      this.busy = true;
      const ok = await this[action](item);
      this.busy = false;
      if (ok) this.pending = null;
    },
    removeItem(item) {
      this.queue = this.queue.filter(q => q.id !== item.id);
    },
    async confirm(item) {
      try {
        await ConfirmationsService.confirm(item.id);
        this.removeItem(item);
        this.$store.dispatch("clinica/refreshBadges");
        this.$toast.success("Consulta confirmada");
        return true;
      } catch (e) {
        this.$toast.error("Não foi possível confirmar a consulta");
        return false;
      }
    },
    async cancel(item) {
      try {
        await ConfirmationsService.cancel(item.id);
        this.removeItem(item);
        this.$store.dispatch("clinica/refreshBadges");
        this.$toast.success("Consulta cancelada");
        return true;
      } catch (e) {
        this.$toast.error("Não foi possível cancelar a consulta");
        return false;
      }
    },
    async reschedule(item) {
      try {
        await ConfirmationsService.toReschedule(item.id);
        this.removeItem(item);
        this.$store.dispatch("clinica/refreshBadges");
        this.$toast.success("Paciente enviado para reagendamento");
        return true;
      } catch (e) {
        this.$toast.error("Não foi possível enviar para reagendamento");
        return false;
      }
    },
    // Bucket temporal de uma consulta a partir da data real (scheduled_at).
    inBucket(item, bucket) {
      if (bucket === "todas") return true;
      if (!item.scheduledAt) return false;
      const d = new Date(item.scheduledAt);
      const now = new Date();
      const startToday = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      );
      const dDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const diff = Math.round((dDay - startToday) / 86400000);
      if (bucket === "hoje") return diff === 0;
      if (bucket === "amanha") return diff === 1;
      if (bucket === "semana") return diff >= 0 && diff <= 6;
      return true;
    }
  },
  computed: {
    // Abas com contagem REAL por data (antes eram counts hardcoded 8/2/3/8) e
    // filtro funcional (antes as abas não filtravam a fila).
    countIn() {
      return bucket => this.queue.filter(i => this.inBucket(i, bucket)).length;
    },
    filters() {
      return [
        { value: "todas", label: "Todas", count: this.queue.length },
        { value: "hoje", label: "Hoje", count: this.countIn("hoje") },
        { value: "amanha", label: "Amanhã", count: this.countIn("amanha") },
        { value: "semana", label: "Esta semana", count: this.countIn("semana") }
      ];
    },
    filteredQueue() {
      return this.queue.filter(i => this.inBucket(i, this.filter)).reverse();
    },
    amanhaCount() {
      return this.countIn("amanha");
    },
    pendingAction() {
      return this.pending ? ACTIONS[this.pending.action] : null;
    }
  }
};
</script>
